import { createUuid } from '@common/utils';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';

import { getPolicyDetail } from '@/api/getPolicyDetail';
import {
  AuthInfiniteQueryOptions,
  AuthQueryOptions,
  UnauthInfiniteQueryOptions,
  UnauthQueryOptions,
} from '@/api/v2';

export const policyQueryOptionsKeys = {
  policyDetail: createUuid(),
};

interface PolicyOptionsProps {
  id: number;
  isLogin: boolean | undefined;
}

interface SearchOptionsProps {
  codeRegion?: string;
  codeSubRegion?: string;
  keyword?: string;
  isLogin?: boolean | undefined;
  descAgeList?: string[];
  codeProvisionTypeList?: string[];
  policySort?: 'LATEST' | 'POPULARITY';
}

export interface PolicySearchItem {
  name: string;
  id: number;
}

export const policyQueryOptions = {
  feedback: ({ id, isLogin }: PolicyOptionsProps) => {
    const options = isLogin
      ? AuthQueryOptions['get:/wello/v2/auth/policy/feedback/{policy_id}']
      : UnauthQueryOptions['get:/wello/v2/allows/policy/feedback/{policy_id}'];

    return queryOptions({
      ...options({
        path: {
          policy_id: id,
        },
      }),
      enabled: isLogin !== undefined,
      placeholderData: keepPreviousData,
      select: ({ context }) => ({
        readingCount: context?.reading_count ?? 0,
        hasNewComment: context?.new_comment_yn ?? false,
        isThumbUp: context?.like_yn ?? false,
        thumbUpCount: context?.like_count ?? 0,
        isBookmarked: context?.wishlist_yn,
        bookmarkCount: context?.wishlist_count ?? 0,
        commentCount: context?.comment_count ?? 0,
        likeCount: context?.like_count ?? 0,
      }),
    });
  },
  policyDetail: (idx: string) =>
    queryOptions({
      queryKey: [policyQueryOptionsKeys.policyDetail, idx],
      queryFn: () => getPolicyDetail(idx),
    }),
  search: ({
    isLogin,
    keyword,
    codeRegion,
    codeSubRegion,
    descAgeList,
    codeProvisionTypeList,
    policySort = 'LATEST',
  }: SearchOptionsProps) => {
    const INITIAL_PAGE = 1;
    const PAGE_SIZE = 20;

    const options = isLogin
      ? AuthInfiniteQueryOptions['get:/wello/v2/auth/policy']
      : UnauthInfiniteQueryOptions['get:/wello/v2/allows/policy'];

    return infiniteQueryOptions({
      ...options({
        query: {
          page: INITIAL_PAGE,
          size: PAGE_SIZE,
          codeProvisionTypeList,
          codeRegion,
          codeSubRegion,
          descAgeList,
          policySort,
          searchKeyword: keyword,
        },
      }),
      enabled: isLogin !== undefined,
      getNextPageParam: (lastPage) => {
        const { page: currentPage, total_count: totalCount } =
          lastPage.context ?? {};

        if (!totalCount || !currentPage) return;

        const nextPage = currentPage + 1;
        const totalPageCount = Math.ceil(totalCount / PAGE_SIZE);

        if (nextPage <= totalPageCount) return nextPage;
      },
      initialPageParam: INITIAL_PAGE,
      placeholderData: keepPreviousData,
    });
  },
};
